import { ProposalTypeByCode } from "@repo/core/constants/proposals";
import { LimitReassessmentEnum } from "@repo/core/enums/proposals";
import { pickFirstValidErpDate } from "@repo/core/utils/erpDate.utils";
import { sanitizeParentNameForApi } from "@repo/core/utils/parentName.utils";
import {
  areNormalizedPhonesEqual,
  joinPhoneDigits
} from "@repo/core/utils/phone.utils";
import { withResolvedClientName } from "@repo/core/utils/proposalClientName.utils";
import { normalizeResidentialPhone2TipoToApiCode } from "@repo/core/utils/residentialPhone2.utils";
import {
  normalizeBirthDateToBackendDate,
  normalizeBirthDateToBackendDateTime
} from "@repo/core/utils/birthDate.utils";
import {
  ConsultProposalResponseDTO,
  PROPOSTA_SITUACAO,
  TIPO_CORRESPONDENCIA,
  TIPO_ENVIO_FATURA,
  TIPO_OPCAO_PAGAMENTO,
  TIPO_PESSOA,
  TIPO_RESIDENCIA,
  UpdateProposalInputDTO
} from "@repo/core/services/entities/Proposals";
import type { PreProposalData } from "@repo/core/screens/PreProposal/store/preProposalStore";

/**
 * Converts ISO date string to dd/MM/yyyy format
 * Returns empty string if date is invalid or "0001-01-01T00:00:00"
 * Parses the ISO string directly to avoid timezone issues
 */
export const formatDateFromISO = (dateString?: string | null): string => {
  if (
    !dateString ||
    dateString === "0001-01-01T00:00:00" ||
    dateString.trim() === ""
  ) {
    return "";
  }

  try {
    // Parse ISO string directly to avoid timezone conversion issues
    // Format: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
    const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!dateMatch) {
      return "";
    }

    const year = parseInt(dateMatch[1], 10);
    const month = parseInt(dateMatch[2], 10);
    const day = parseInt(dateMatch[3], 10);

    // Validate date values
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return "";
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return "";
    }

    return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
  } catch {
    return "";
  }
};

/**
 * Converts ISO date string to YYYY-MM-DD format
 * Returns undefined if date is invalid or "0001-01-01T00:00:00"
 * Parses the ISO string directly to avoid timezone issues
 */
export const convertISOToYYYYMMDD = (
  dateString?: string | null
): string | undefined => {
  return normalizeBirthDateToBackendDate(dateString);
};

const convertDDMMYYYYToISO = (
  dateString?: string | null
): string | undefined => {
  return normalizeBirthDateToBackendDateTime(dateString);
};

/**
 * Removes formatting from CPF/CNPJ (removes dots, dashes, and slashes)
 * Returns only numbers
 */
const removeCpfCnpjFormatting = (value?: string | null): string => {
  if (!value) return "";
  return value.replace(/[.\-/]/g, "");
};

type CreditProposalFieldAliases = Partial<ConsultProposalResponseDTO> & {
  strAtivSetor?: string;
  strAtivDataAdmissao?: string;
  strRenda?: number;
  dblAtivRenda?: number;
  strEndDdd1?: string;
  strEndFone1?: string;
  strEndDdd2?: string;
  strEndFone2?: string;
  strEndTipoFone2?: string;
};

const normalizeCpfDigits = (value?: string | null): string =>
  (value || "").replace(/\D/g, "");

const pickFirstNonEmptyString = (
  ...values: Array<string | undefined | null>
): string | undefined => {
  for (const value of values) {
    const normalized = value?.trim();
    if (normalized) {
      return normalized;
    }
  }
  return undefined;
};

const resolveAdditionalPhoneFieldForPersist = (
  activeValue: string | undefined | null,
  ...fallbackValues: Array<string | undefined | null>
): string | undefined =>
  activeValue !== undefined && activeValue !== null
    ? activeValue.trim()
    : pickFirstNonEmptyString(...fallbackValues);

/** Whether pre-proposal store belongs to the same credit proposal context. */
export const isMatchingPreProposalContext = (
  snapshot: Partial<ConsultProposalResponseDTO>,
  preProposalData?: PreProposalData | null
): boolean => {
  if (!preProposalData) {
    return false;
  }

  if (
    preProposalData.lngProposta != null &&
    snapshot.lngProposta != null &&
    String(preProposalData.lngProposta) === String(snapshot.lngProposta)
  ) {
    return true;
  }

  const preCpf = normalizeCpfDigits(preProposalData.strCliente);
  const snapCpf = normalizeCpfDigits(snapshot.strCpf || snapshot.strCliente);

  return preCpf.length > 0 && preCpf === snapCpf;
};

/**
 * Merges cross-step context (e.g. mobile phone from pre-proposal token flow)
 * into the credit store snapshot before draft persistence.
 */
export const enrichCreditProposalSnapshotForPersist = (
  snapshot: CreditProposalFieldAliases,
  preProposalData?: PreProposalData | null
): CreditProposalFieldAliases => {
  const fromPreProposal = isMatchingPreProposalContext(
    snapshot,
    preProposalData
  )
    ? {
        strEndDdd1: preProposalData?.strEndDdd1,
        strEndFone1: preProposalData?.strEndFone1,
        strEndDdd2: preProposalData?.strEndDdd2,
        strEndFone2: preProposalData?.strEndFone2,
        strEndTipoFone2: preProposalData?.strEndTipoFone2,
        strResDDD2: preProposalData?.strResDDD2,
        strResFone2: preProposalData?.strResFone2,
        strResTipoFone2: preProposalData?.strResTipoFone2,
        intDiaCartao: preProposalData?.intDiaCartao,
        datDataNascimento: convertDDMMYYYYToISO(preProposalData?.datNascimento),
        strClasseProfissional: preProposalData?.strClasseProfissional,
        strProfissao: preProposalData?.strProfissao,
        strAtivProfissao: preProposalData?.strAtivProfissao,
        strAtivSetor: preProposalData?.strAtivSetor,
        strTrabalhaDesde: preProposalData?.strTrabalhaDesde,
        strAtivDataAdmissao: preProposalData?.strAtivDataAdmissao,
        datAtivDataAdmissao: preProposalData?.datAtivDataAdmissao,
        datTrabDesde: preProposalData?.datTrabDesde,
        strRenda: preProposalData?.strRenda,
        dblRenda: preProposalData?.dblRenda,
        dblComSalario: preProposalData?.dblComSalario
      }
    : {};
  const hasSnapshotBirthDate = !!formatDateFromISO(snapshot.datDataNascimento);

  const merged: CreditProposalFieldAliases = {
    ...snapshot,
    strEndDdd1: snapshot.strEndDdd1 ?? fromPreProposal.strEndDdd1,
    strEndFone1: snapshot.strEndFone1 ?? fromPreProposal.strEndFone1,
    strEndDdd2: resolveAdditionalPhoneFieldForPersist(
      snapshot.strEndDdd2,
      snapshot.strResDDD2,
      fromPreProposal.strEndDdd2,
      fromPreProposal.strResDDD2
    ),
    strEndFone2: resolveAdditionalPhoneFieldForPersist(
      snapshot.strEndFone2,
      snapshot.strResFone2,
      fromPreProposal.strEndFone2,
      fromPreProposal.strResFone2
    ),
    strEndTipoFone2: resolveAdditionalPhoneFieldForPersist(
      snapshot.strEndTipoFone2,
      snapshot.strResTipoFone2,
      fromPreProposal.strEndTipoFone2,
      fromPreProposal.strResTipoFone2
    ),
    intDiaCartao: pickFirstDefinedNumber(
      snapshot.intDiaCartao,
      fromPreProposal.intDiaCartao
    ),
    strClasseProfissional: pickFirstNonEmptyString(
      snapshot.strClasseProfissional,
      fromPreProposal.strClasseProfissional
    ),
    strProfissao: pickFirstNonEmptyString(
      snapshot.strProfissao,
      snapshot.strAtivProfissao,
      snapshot.strAtivSetor,
      fromPreProposal.strProfissao,
      fromPreProposal.strAtivProfissao,
      fromPreProposal.strAtivSetor
    ),
    strAtivProfissao: pickFirstNonEmptyString(
      snapshot.strAtivProfissao,
      snapshot.strProfissao,
      snapshot.strAtivSetor,
      fromPreProposal.strAtivProfissao,
      fromPreProposal.strProfissao,
      fromPreProposal.strAtivSetor
    ),
    strTrabalhaDesde: pickFirstValidErpDate(
      snapshot.strTrabalhaDesde,
      snapshot.strAtivDataAdmissao,
      snapshot.datAtivDataAdmissao,
      snapshot.datTrabDesde,
      fromPreProposal.strTrabalhaDesde,
      fromPreProposal.strAtivDataAdmissao,
      fromPreProposal.datAtivDataAdmissao,
      fromPreProposal.datTrabDesde
    ),
    datAtivDataAdmissao: pickFirstValidErpDate(
      snapshot.datAtivDataAdmissao,
      snapshot.strAtivDataAdmissao,
      snapshot.strTrabalhaDesde,
      snapshot.datTrabDesde,
      fromPreProposal.datAtivDataAdmissao,
      fromPreProposal.strAtivDataAdmissao,
      fromPreProposal.strTrabalhaDesde,
      fromPreProposal.datTrabDesde
    ),
    dblRenda: pickFirstPositiveNumber(
      snapshot.dblRenda,
      snapshot.dblComSalario,
      snapshot.strRenda,
      fromPreProposal.dblRenda,
      fromPreProposal.dblComSalario,
      fromPreProposal.strRenda
    ),
    dblComSalario: pickFirstPositiveNumber(
      snapshot.dblComSalario,
      snapshot.dblRenda,
      snapshot.strRenda,
      fromPreProposal.dblComSalario,
      fromPreProposal.dblRenda,
      fromPreProposal.strRenda
    ),
    datDataNascimento: hasSnapshotBirthDate
      ? snapshot.datDataNascimento
      : fromPreProposal.datDataNascimento
  };

  const { ddd, phone } = resolveCreditProposalMobilePhone(merged);

  if (!phone) {
    return merged;
  }

  return {
    ...merged,
    strCelular: merged.strCelular?.trim() || phone,
    strCelularDDD: merged.strCelularDDD?.trim() || ddd,
    strEndDdd1: ddd,
    strEndFone1: phone
  };
};

/** Mobile phone for credit proposal save — multiple API/store field names. */
export const resolveCreditProposalMobilePhone = (
  proposal: CreditProposalFieldAliases
): { ddd: string; phone: string } => {
  const ddd = (
    proposal.strCelularDDD ||
    proposal.strDDDEnvioToken ||
    proposal.strEndDdd1 ||
    proposal.strResDDD ||
    ""
  ).trim();

  const phone = (
    proposal.strCelular ||
    proposal.strFoneEnvioToken ||
    proposal.strEndFone1 ||
    proposal.strResFone ||
    ""
  ).trim();

  return { ddd, phone };
};

const pickFirstDefinedNumber = (
  ...values: Array<number | undefined | null>
): number | undefined => {
  for (const value of values) {
    if (value !== undefined && value !== null && !Number.isNaN(value)) {
      return value;
    }
  }
  return undefined;
};

const pickFirstPositiveNumber = (
  ...values: Array<number | undefined | null>
): number | undefined => {
  for (const value of values) {
    if (
      value !== undefined &&
      value !== null &&
      !Number.isNaN(value) &&
      value > 0
    ) {
      return value;
    }
  }
  return undefined;
};

/** Profession code from ConsultarProposta / reanalysis payload aliases. */
export const resolveProfessionCode = (
  proposal: CreditProposalFieldAliases
): string =>
  (
    proposal.strProfissao ||
    proposal.strAtivProfissao ||
    proposal.strAtivSetor ||
    ""
  ).trim();

/** Spec #32: strTrabalhaDesde from ConsultarProposta (ignore ERP empty sentinel). */
export const resolveAdmissionDateIso = (
  proposal: CreditProposalFieldAliases
): string | undefined =>
  pickFirstValidErpDate(
    proposal.strTrabalhaDesde,
    proposal.datAtivDataAdmissao,
    proposal.strAtivDataAdmissao,
    proposal.datTrabDesde
  );

/** Spec #33: dblRenda from ConsultarProposta. */
export const resolveIncomeAmount = (
  proposal: CreditProposalFieldAliases
): number | undefined =>
  pickFirstPositiveNumber(
    proposal.dblRenda,
    proposal.dblComSalario,
    proposal.strRenda,
    proposal.dblAtivRenda
  );

/** Selected invoice due day. The invoice screen must not treat intDiaVencimento as a user choice. */
export const resolveCardDay = (
  proposal: Partial<ConsultProposalResponseDTO>
): number | undefined => pickFirstDefinedNumber(proposal.intDiaCartao);

export interface MapCreditProposalToSavePayloadOptions {
  isReanalysis?: boolean;
  statusOverride?: PROPOSTA_SITUACAO;
  requirePhone?: boolean;
  /**
   * When true, forces strSituacao to DATA_CONFERENCE ("20") and adds
   * blnManterPendente=true so the backend keeps the proposal as a draft.
   * Used by the per-step persistence flow to sync the store with the API
   * after every screen advance.
   */
  asDraft?: boolean;
}

/**
 * Maps credit proposal data from store to UpdateProposalInputDTO for saving
 * Only includes the specific fields required for proposal update
 */
export const mapCreditProposalToSavePayload = (
  proposalData: CreditProposalFieldAliases,
  options?: MapCreditProposalToSavePayloadOptions
): UpdateProposalInputDTO => {
  if (!proposalData.lngProposta) {
    throw new Error("lngProposta is required");
  }

  // Exclude frontend-only fields that should not be sent to backend
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    strProfissaoDescricao: _strProfissaoDescricao,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    strClasseProfissionalDescricao: _strClasseProfissionalDescricao,
    ...restProposalData
  } = proposalData as Partial<
    CreditProposalFieldAliases & {
      strProfissaoDescricao?: string;
      strClasseProfissionalDescricao?: string;
    }
  >;

  const cpfOrCnpj =
    restProposalData.strCpf || restProposalData.strCliente || "";
  const strClienteClean = removeCpfCnpjFormatting(cpfOrCnpj);

  const datNascimento = convertISOToYYYYMMDD(
    restProposalData.datDataNascimento
  );
  const admissionDateIso = resolveAdmissionDateIso(restProposalData);
  const datAtivDataAdmissao = convertISOToYYYYMMDD(admissionDateIso);
  const professionCode = resolveProfessionCode(restProposalData);
  const incomeAmount = resolveIncomeAmount(restProposalData);
  const cardDay = resolveCardDay(restProposalData);
  const strEndDdd2 = (
    restProposalData.strEndDdd2 ?? restProposalData.strResDDD2
  )?.trim();
  const strEndFone2 = (
    restProposalData.strEndFone2 ?? restProposalData.strResFone2
  )?.trim();
  const strEndTipoFone2 = normalizeResidentialPhone2TipoToApiCode(
    restProposalData.strEndTipoFone2 ?? restProposalData.strResTipoFone2
  );
  const { ddd: strEndDdd1, phone: strEndFone1 } =
    resolveCreditProposalMobilePhone(restProposalData);
  const hasAdditionalPhone =
    Boolean(strEndDdd2 && strEndFone2) &&
    !areNormalizedPhonesEqual(
      joinPhoneDigits(strEndDdd2, strEndFone2),
      joinPhoneDigits(strEndDdd1, strEndFone1)
    );

  const requirePhone = options?.requirePhone ?? !options?.asDraft;
  if (requirePhone) {
    if (!strEndDdd1) {
      throw new Error(
        "DDD do telefone é obrigatório. Verifique se os dados da proposta foram carregados corretamente."
      );
    }
    if (!strEndFone1) {
      throw new Error(
        "Número do telefone é obrigatório. Verifique se os dados da proposta foram carregados corretamente."
      );
    }
  }

  // Remove formatting from CEP (remove dashes)
  const formatCep = (cep?: string | null): string | undefined => {
    if (!cep) return undefined;
    return cep.replace(/-/g, "");
  };

  const strEstCivil = restProposalData.strEstadoCivil;
  const motherName = sanitizeParentNameForApi(restProposalData.strNomeMae);
  const fatherName = sanitizeParentNameForApi(restProposalData.strNomePai);
  const strResNumero = (
    restProposalData.strResNumero ||
    restProposalData.strNumero ||
    ""
  )
    .replace(/\D/g, "")
    .slice(0, 7);

  // Build payload with only the required fields
  const payload: UpdateProposalInputDTO = {
    // Fields already filled in pre-proposal
    lngProposta: String(restProposalData.lngProposta),
    strSituacao:
      options?.statusOverride ||
      (restProposalData.strSituacao as PROPOSTA_SITUACAO) ||
      "",
    strNome: restProposalData.strNome || "",
    strCliente: strClienteClean,
    strNroDocIdent:
      restProposalData.strNroDocIdent &&
      restProposalData.strNroDocIdent.trim() !== ""
        ? removeCpfCnpjFormatting(restProposalData.strNroDocIdent)
        : strClienteClean,
    ...(strEndDdd1 && strEndFone1 ? { strEndDdd1, strEndFone1 } : {}),
    strAtendente: restProposalData.strAtendente || "",
    strProduto: restProposalData.strProduto || "",
    strBandeira: restProposalData.strBandeira || "",
    strDigitador: restProposalData.strDigitador || "",
    strUsuarioEfetivacao: restProposalData.strUsuarioEfetivacao || "",
    strPontoAtendimento: restProposalData.strPontoAtendimento || "",
    strRefPessNome1: restProposalData.strRefPessNome1 || "",
    strRefPessRel1: restProposalData.strRefPessRel1 || "",
    strRefPessDdd1: restProposalData.strRefPessDdd1 || "",
    strRefPessFone1: restProposalData.strRefPessFone1 || "",

    // Step 1 - Additional personal data
    ...(datNascimento && { datNascimento }),
    // Always include strEstCivil if it exists (required field by API)
    ...(strEstCivil !== undefined && strEstCivil !== null
      ? { strEstCivil: String(strEstCivil).trim() }
      : {}),
    // Always include strSexo if it exists (required field by API)
    ...(restProposalData.strSexo !== undefined &&
    restProposalData.strSexo !== null &&
    String(restProposalData.strSexo).trim() !== ""
      ? {
          strSexo:
            String(restProposalData.strSexo).trim() === "M" ||
            String(restProposalData.strSexo).trim() === "F"
              ? String(restProposalData.strSexo).trim()
              : String(restProposalData.strSexo).trim()
        }
      : {}),
    ...(motherName && {
      strNomeMae: motherName
    }),
    ...(fatherName && {
      strNomePai: fatherName
    }),

    // Step 2 - Residential address
    ...(formatCep(restProposalData.strResCep || restProposalData.strCep) && {
      strResCep: formatCep(
        restProposalData.strResCep || restProposalData.strCep
      )
    }),
    ...((restProposalData.strResEndereco || restProposalData.strEndereco) && {
      strResEndereco:
        restProposalData.strResEndereco || restProposalData.strEndereco
    }),
    ...(strResNumero ? { strResNumero } : {}),
    ...((restProposalData.strResComplemento ||
      restProposalData.strComplemento) && {
      strResComplemento:
        restProposalData.strResComplemento || restProposalData.strResComplemento
    }),
    ...((restProposalData.strResCidade || restProposalData.strCidade) && {
      strResCidade: restProposalData.strResCidade || restProposalData.strCidade
    }),
    ...((restProposalData.strResBairro || restProposalData.strBairro) && {
      strResBairro: restProposalData.strResBairro || restProposalData.strBairro
    }),
    ...((restProposalData.strResUF || restProposalData.strUf) && {
      strResUF: restProposalData.strResUF || restProposalData.strUf
    }),
    ...(restProposalData.strResPais && {
      strResPais: restProposalData.strResPais
    }),

    ...(hasAdditionalPhone ? { strEndDdd2, strEndFone2, strEndTipoFone2 } : {}),

    // Step 3 - Professional activity
    ...(professionCode ? { strAtivProfissao: professionCode } : {}),
    ...(restProposalData.strClasseProfissional && {
      strClasseProfissional: restProposalData.strClasseProfissional
    }),
    ...(datAtivDataAdmissao && {
      datAtivDataAdmissao
    }),
    ...(incomeAmount !== undefined
      ? {
          dblComSalario: incomeAmount,
          dblRenda: incomeAmount,
          strRenda: incomeAmount
        }
      : {}),

    // Step 4 - Invoice
    ...(cardDay !== undefined ? { intDiaCartao: cardDay } : {}),

    // Invoice delivery channel: E (Email), W (App), T (SMS), C (Correio).
    // Contact email is always sent when available, independent of delivery channel.
    ...(restProposalData.strEnvioFatura
      ? {
          strEnvioFatura: restProposalData.strEnvioFatura as
            | "E"
            | "W"
            | "T"
            | "C",
          ...(restProposalData.strEmail
            ? {
                strInfoEmail: restProposalData.strEmail,
                strEmail: restProposalData.strEmail
              }
            : {})
        }
      : restProposalData.strEmail
        ? {
            strEnvioFatura: TIPO_ENVIO_FATURA.EMAIL,
            strInfoEmail: restProposalData.strEmail,
            strEmail: restProposalData.strEmail
          }
        : {}),

    // Fixed fields (overridable by store values where applicable)
    strTipoPessoa: TIPO_PESSOA.FISICA,
    strEndTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL,
    strResTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL,
    strEndTipoResidencia: restProposalData.strEndTipoResidencia || "1",
    strOpcaoPagamentos: TIPO_OPCAO_PAGAMENTO.BANCO,
    strTipoCorrespondencia: TIPO_CORRESPONDENCIA.RESIDENCIAL,
    strTipoProposta: ProposalTypeByCode.AMBOS,

    // Revaluation of limit - required when client has active card (reanalysis flow)
    strReavaliacaoLimite: options?.isReanalysis
      ? LimitReassessmentEnum.REANALYSIS
      : LimitReassessmentEnum.ANALYSIS
  };

  if (options?.asDraft) {
    payload.strSituacao = PROPOSTA_SITUACAO.DATA_CONFERENCE;
    payload.blnManterPendente = true;
  }

  return payload;
};

export const mapCreditProposalToStoreData = (
  proposalData: CreditProposalFieldAliases
): CreditProposalFieldAliases => {
  const withName = withResolvedClientName(proposalData);
  const aliases = withName as CreditProposalFieldAliases;
  const professionCode = resolveProfessionCode(aliases);
  const admissionDateIso = resolveAdmissionDateIso(aliases);
  const incomeAmount = resolveIncomeAmount(aliases);
  const cardDay = resolveCardDay(withName);
  const { ddd: mobileDdd, phone: mobilePhone } =
    resolveCreditProposalMobilePhone(aliases);
  const additionalPhoneDdd = pickFirstNonEmptyString(
    aliases.strEndDdd2,
    aliases.strResDDD2
  );
  const additionalPhoneNumber = pickFirstNonEmptyString(
    aliases.strEndFone2,
    aliases.strResFone2
  );
  const additionalPhoneType = pickFirstNonEmptyString(
    aliases.strEndTipoFone2,
    aliases.strResTipoFone2
  );

  return {
    ...withName,
    ...(mobilePhone && !withName.strCelular?.trim()
      ? {
          strCelular: mobilePhone,
          strCelularDDD: mobileDdd
        }
      : {}),
    strClasseProfissional:
      withName.strClasseProfissional == "0"
        ? ""
        : withName.strClasseProfissional,
    ...(professionCode
      ? {
          strProfissao: professionCode,
          strAtivProfissao: professionCode
        }
      : {}),
    ...(admissionDateIso
      ? {
          strTrabalhaDesde: admissionDateIso,
          datTrabDesde: admissionDateIso,
          datAtivDataAdmissao: admissionDateIso
        }
      : {}),
    ...(incomeAmount !== undefined
      ? { dblRenda: incomeAmount, dblComSalario: incomeAmount }
      : {}),
    ...(cardDay !== undefined ? { intDiaCartao: cardDay } : {}),
    ...(additionalPhoneDdd ? { strEndDdd2: additionalPhoneDdd } : {}),
    ...(additionalPhoneNumber ? { strEndFone2: additionalPhoneNumber } : {}),
    ...(additionalPhoneType ? { strEndTipoFone2: additionalPhoneType } : {})
  };
};
